import {AppTopNav} from 'app/modules/shared/AppTopNav'
import {BasePageProps, Sponsor} from 'app/types'
import React, {useState, useEffect, useCallback} from 'react'
import {Modal, Button, Alert} from 'react-bootstrap'
import {Formik, Form, Field, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import {SponsorService} from 'app/services/SponsorService'
import {AppModal} from 'app/modules/shared/modals/AppModal'

const ManageSponsors: React.FC<BasePageProps> = (props: BasePageProps) => {
  const [sponsors, setSponsors] = useState<Sponsor[]>([])
  const [form, setForm] = useState<Partial<Sponsor>>({
    name: '',
    description: '',
    subdomain: '',
    sponsoredByText: '',
    jobFairPaymentMode: 'employer',
  })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSubdomainChangeModal, setShowSubdomainChangeModal] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined)
  const [pageLimit] = useState(9)

  const sponsorService = new SponsorService()

  const getBaseDomain = () => {
    const hostname = window.location.hostname
    const parts = hostname.split('.')
    if (parts.length > 2) {
      return parts.slice(1).join('.')
    }
    return hostname
  }

  const getSponsorUrl = (subdomain: string) => {
    const {protocol, host, hostname} = window.location
    const parts = host.split('.')
    if (parts.length > 2 || (parts.length === 1 && hostname === 'localhost')) {
      const domain = parts.length > 1 ? parts.slice(1).join('.') : parts[0]
      return `${protocol}//${subdomain}.${domain}`
    }
    return `${protocol}//${subdomain}.${host}`
  }

  const fetchSponsors = useCallback(
    async (cursor?: string) => {
      setLoading(true)
      try {
        const {sponsors: newSponsors, cursor: newNextCursor} = await sponsorService.getAllSponsors(
          pageLimit,
          cursor
        )
        setSponsors(cursor ? [...sponsors, ...newSponsors] : newSponsors) // Append new sponsors if cursor, else replace
        setNextCursor(newNextCursor)
      } catch (error) {
        console.error('Error fetching sponsors:', error)
      } finally {
        setLoading(false)
      }
    },
    [sponsors, pageLimit]
  )

  useEffect(() => {
    fetchSponsors()
  }, [])

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required').max(100, 'Name must be at most 100 characters'),
    subdomain: Yup.string()
      .required('Subdomain is required')
      .max(50, 'Subdomain must be at most 50 characters')
      .matches(
        /^[a-z0-9-]+$/,
        'Subdomain must be lowercase and can only contain letters, numbers, and hyphens'
      )
      .lowercase('Subdomain must be lowercase'),
    description: Yup.string().max(500, 'Description must be at most 500 characters'),
    sponsoredByText: Yup.string()
      .required('Tagline is required')
      .max(500, 'Tagline must be at most 500 characters'),
  })

  const handleSubmitFormik = async (values: Partial<Sponsor>) => {
    setLoading(true)
    try {
      if (editingId) {
        const updatedSponsor = await sponsorService.updateSponsor(editingId, values)
        setSponsors((prev) =>
          prev.map((sponsor) => (sponsor.id === editingId ? updatedSponsor : sponsor))
        )
        setEditingId(null)
      } else {
        await sponsorService.createSponsor(values)
        fetchSponsors()
      }
    } catch (error) {
      console.error('Error saving sponsor:', error)
    } finally {
      setLoading(false)
      closeModal()
    }
  }

  const handleEdit = (id: string) => {
    const sponsor = sponsors.find((s) => s.id === id)
    if (sponsor) {
      setForm(sponsor)
      setEditingId(id)
      openModal()
    }
  }

  const handleDelete = async () => {
    if (deleteId) {
      setLoading(true)
      try {
        await sponsorService.deleteSponsor(deleteId)
        fetchSponsors()
      } catch (error) {
        console.error('Error deleting sponsor:', error)
      } finally {
        setLoading(false)
        setDeleteId(null)
        setShowDeleteModal(false)
      }
    }
  }

  const openModal = () => setShowModal(true)
  const closeModal = () => {
    setShowModal(false)
    setEditingId(null)
    setForm({name: '', description: '', subdomain: '', sponsoredByText: '', jobFairPaymentMode: 'employer'})
  }

  const openDeleteModal = (id: string) => {
    setDeleteId(id)
    setShowDeleteModal(true)
  }

  const closeDeleteModal = () => {
    setDeleteId(null)
    setShowDeleteModal(false)
  }

  const handleLoadMore = () => {
    if (nextCursor) {
      fetchSponsors(nextCursor)
    }
  }

  return (
    <div className='chatPage'>
      <AppTopNav title={props.title} breadcrumbs={props.breadcrumbs} />

      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_toolbar' className='app-toolbar py-3 py-lg-6'>
          <div
            id='kt_app_toolbar_container'
            className='app-container container-xxl d-flex flex-stack'
          >
            <div className='page-title d-flex flex-column justify-content-center flex-wrap me-3'>
              <h1 className='page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0'>
                Sponsors Management
              </h1>
              <ul className='breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1'>
                <li className='breadcrumb-item text-muted'>
                  <span className='text-muted'>Dashboard</span>
                </li>
                <li className='breadcrumb-item'>
                  <span className='bullet bg-gray-400 w-5px h-2px'></span>
                </li>
                <li className='breadcrumb-item text-muted'>Sponsors</li>
              </ul>
            </div>
            <div className='d-flex align-items-center gap-2 gap-lg-3'>
              <button
                className='btn btn-sm fw-bold btn-primary'
                onClick={openModal}
                disabled={loading}
              >
                <i className='bi bi-plus fs-2'></i>
                Add New Sponsor
              </button>
            </div>
          </div>
        </div>

        <div id='kt_app_content' className='app-content flex-column-fluid'>
          <div id='kt_app_content_container' className='app-container container-xxl'>
            <div className='card card-flush'>
              <div className='card-header align-items-center py-5 gap-2 gap-md-5'>
                <div className='card-title'>
                  <div className='d-flex align-items-center position-relative my-1'>
                    <h3 className='fw-bold m-0'>All Sponsors</h3>
                    <span className='badge badge-secondary ms-3'>{sponsors?.length || 0}</span>
                  </div>
                </div>
              </div>

              <div className='card-body pt-0'>
                {loading ? (
                  <div className='d-flex flex-center' style={{minHeight: '300px'}}>
                    <div className='spinner-border spinner-border-lg text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                ) : sponsors && sponsors.length > 0 ? (
                  <>
                    <div className='row g-6 g-xl-9'>
                      {sponsors.map((sponsor) => (
                        <div key={sponsor.id} className='col-md-6 col-xl-4'>
                          <div className='card card-flush h-md-100 card-bordered'>
                            <div className='card-header'>
                              <div className='card-title d-flex flex-column'>
                                <div className='d-flex align-items-center'>
                                  <div className='symbol symbol-40px me-3'>
                                    <div className='symbol-label bg-light-primary'>
                                      <i className='bi bi-building fs-2 text-primary'></i>
                                    </div>
                                  </div>
                                  <div className='flex-grow-1'>
                                    <a
                                      href={getSponsorUrl(sponsor.subdomain)}
                                      target='_blank'
                                      rel='noreferrer'
                                      className='text-gray-800 text-hover-primary fw-bold fs-4'
                                    >
                                      {sponsor.name}
                                    </a>
                                  </div>
                                </div>
                              </div>
                              <div className='card-toolbar'>
                                <div className='dropdown'>
                                  <button
                                    className='btn btn-sm btn-icon btn-color-primary btn-active-light-primary'
                                    data-bs-toggle='dropdown'
                                  >
                                    <i className='ki-duotone ki-dots-vertical fs-5'>
                                      <span className='path1'></span>
                                      <span className='path2'></span>
                                      <span className='path Lunatic3'></span>
                                    </i>
                                  </button>
                                  <div className='dropdown-menu dropdown-menu-end'>
                                    <button
                                      className='dropdown-item'
                                      onClick={() => handleEdit(sponsor.id)}
                                      disabled={loading}
                                    >
                                      <i className='ki-duotone ki-pencil fs-5 me-3'>
                                        <span className='path1'></span>
                                        <span className='path2'></span>
                                      </i>
                                      Edit
                                    </button>
                                    <button
                                      className='dropdown-item text-danger'
                                      onClick={() => openDeleteModal(sponsor.id)}
                                      disabled={loading}
                                    >
                                      <i className='ki-duotone ki-trash fs-5 me-3'>
                                        <span className='path1'></span>
                                        <span className='path2'></span>
                                        <span className='path3'></span>
                                        <span className='path4'></span>
                                        <span className='path5'></span>
                                      </i>
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className='card-body pt-0'>
                              <div className='fw-semibold text-gray-600 mb-5'>
                                {sponsor.description}
                              </div>

                              <div className='d-flex flex-stack fs-6 py-3'>
                                <div className='fw-semibold pe-5'>
                                  <span className='text-gray-400'>Subdomain:</span>
                                </div>
                                <div className='text-gray-600 text-end'>
                                  <span className='badge badge-light-info'>
                                    {sponsor.subdomain}
                                  </span>
                                </div>
                              </div>

                              <div className='separator separator-dashed my-3'></div>

                              <div className='d-flex flex-stack fs-6 py-3'>
                                <div className='fw-semibold pe-5'>
                                  <span className='text-gray-400'>Sponsored By:</span>
                                </div>
                                <div className='text-gray-600 text-end'>
                                  {sponsor.sponsoredByText}
                                </div>
                              </div>

                              <div className='separator separator-dashed my-3'></div>

                              <div className='d-flex flex-stack fs-6 py-3'>
                                <div className='fw-semibold pe-5'>
                                  <span className='text-gray-400'>Job Fair Billing:</span>
                                </div>
                                <div className='text-gray-600 text-end'>
                                  {sponsor.jobFairPaymentMode === 'sponsor' ? 'Sponsor invoiced' : 'Employer pays'}
                                </div>
                              </div>
                            </div>

                            <div className='card-footer d-flex justify-content-end'>
                              <button
                                className='btn btn-sm btn-light btn-color-muted btn-active-light-primary me-2'
                                onClick={() => handleEdit(sponsor.id)}
                                disabled={loading}
                              >
                                <i className='bi bi-pencil fs-5 me-1'></i>
                                Edit
                              </button>
                              <button
                                className='btn btn-sm btn-light btn-color-muted btn-active-light-danger'
                                onClick={() => openDeleteModal(sponsor.id)}
                                disabled={loading}
                              >
                                <i className='bi bi-trash fs-5 me-1'></i>
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {nextCursor && (
                      <div className='d-flex justify-content-center mt-5'>
                        <button
                          className='btn btn-primary'
                          onClick={handleLoadMore}
                          disabled={loading}
                        >
                          {loading ? 'Loading...' : 'Load More'}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className='d-flex flex-column flex-center' style={{minHeight: '400px'}}>
                    <img src='/media/illustrations/sketchy-1/5.png' className='mw-400px' alt='' />
                    <div className='fs-1 fw-bolder text-dark mb-4'>No Sponsors Found</div>
                    <div className='fs-6'>Start by adding your first sponsor to get started</div>
                    <button className='btn btn-primary mt-5' onClick={openModal} disabled={loading}>
                      <i className='bi bi-plus fs-2 me-2'></i>
                      Add Your First Sponsor
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Create/Edit */}
      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Edit Sponsor' : 'Add Sponsor'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={form}
            validationSchema={validationSchema}
            onSubmit={handleSubmitFormik}
            enableReinitialize
          >
            {({isSubmitting, setFieldValue}) => (
              <Form className='row g-3'>
                <div className='col-12'>
                  <label htmlFor='name' className='form-label'>
                    Name
                  </label>
                  <Field
                    type='text'
                    id='name'
                    name='name'
                    className='form-control'
                    placeholder='Name'
                  />
                  <ErrorMessage name='name' component='div' className='text-danger' />
                </div>
                <div className='col-12'>
                  <label htmlFor='subdomain' className='form-label fw-bold'>
                    Subdomain
                  </label>
                  <div className='input-group'>
                    <Field
                      type='text'
                      id='subdomain'
                      name='subdomain'
                      className={`form-control ${form.subdomain ? 'bg-light' : ''}`}
                      placeholder='Subdomain'
                      readOnly={!!form.subdomain}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
                        setFieldValue('subdomain', value)
                      }}
                    />
                    <span className='input-group-text'>.{getBaseDomain()}</span>
                    {!!form.subdomain && editingId && (
                      <Button
                        variant='outline-primary'
                        type='button'
                        disabled={loading}
                        onClick={() => setShowSubdomainChangeModal(true)}
                      >
                        <i className='bi bi-pencil-square me-1'></i>
                        Change
                      </Button>
                    )}
                  </div>
                  {form.subdomain && editingId && (
                    <div className='text-muted fs-8 mt-2 bg-light-info p-2 rounded border border-info border-dashed'>
                      <i className='bi bi-info-circle-fill text-info me-2'></i>
                      Subdomains are tied to DNS and cannot be renamed. To use a different name, you
                      must first release the current one.
                    </div>
                  )}
                  <ErrorMessage name='subdomain' component='div' className='text-danger mt-1' />
                </div>
                <div className='col-12'>
                  <label htmlFor='description' className='form-label'>
                    Description
                  </label>
                  <Field
                    as='textarea'
                    id='description'
                    name='description'
                    className='form-control'
                    placeholder='Description'
                  />
                  <ErrorMessage name='description' component='div' className='text-danger' />
                </div>
                <div className='col-12'>
                  <label htmlFor='sponsoredByText' className='form-label'>
                    Sponsored By Text
                  </label>
                  <Field
                    type='text'
                    id='sponsoredByText'
                    name='sponsoredByText'
                    className='form-control'
                    placeholder='Sponsored By Text'
                  />
                  <ErrorMessage name='sponsoredByText' component='div' className='text-danger' />
                </div>
                <div className='col-12'>
                  <label htmlFor='jobFairPaymentMode' className='form-label'>
                    Job Fair Billing
                  </label>
                  <Field as='select' id='jobFairPaymentMode' name='jobFairPaymentMode' className='form-select'>
                    <option value='employer'>Employer pays via Payment Gateway</option>
                    <option value='sponsor'>Sponsor invoiced outside VJF</option>
                  </Field>
                  <div className='form-text'>Companies linked to sponsor-invoiced sponsors bypass Payment gateway.</div>
                </div>
                <div className='col-12 text-end'>
                  <Button type='submit' variant='primary' disabled={isSubmitting || loading}>
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </div>
                <AppModal
                  show={showSubdomainChangeModal}
                  title='Release Subdomain?'
                  variant='YesNo'
                  onHide={() => setShowSubdomainChangeModal(false)}
                  onReject={() => setShowSubdomainChangeModal(false)}
                  onSuccess={async () => {
                    setLoading(true)
                    try {
                      if (editingId) {
                        await sponsorService.deleteSubdomainAssociation(editingId)
                        setFieldValue('subdomain', '')
                        setForm({...form, subdomain: ''})
                      }
                      setShowSubdomainChangeModal(false)
                    } catch (error) {
                      console.error('Failed to release subdomain:', error)
                    } finally {
                      setLoading(false)
                    }
                  }}
                  successText='Release and Change'
                  rejectText='Keep Current'
                  successBtnVariant='danger'
                >
                  <div className='text-center'>
                    <i className='bi bi-exclamation-triangle text-danger fs-3x mb-3'></i>
                    <p>
                      Releasing{' '}
                      <strong>
                        {form.subdomain}.{getBaseDomain()}
                      </strong>{' '}
                      will immediately make the current URL inactive.
                    </p>
                    <p className='text-muted'>
                      You will then be able to enter a new subdomain which will take a few moments
                      to activate.
                    </p>
                  </div>
                </AppModal>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>

      {/* Confirmation Modal for Delete */}
      <AppModal
        show={showDeleteModal}
        title='Confirm Delete'
        variant='YesNo'
        onHide={closeDeleteModal}
        onReject={closeDeleteModal}
        onSuccess={handleDelete}
        successText='Yes, Delete'
        rejectText='Cancel'
        successBtnVariant='danger'
      >
        Are you sure you want to delete this sponsor?
      </AppModal>
    </div>
  )
}

export default ManageSponsors
